import type { SideMenuItem } from "@sumprio/ui";
import { permissionFlagEnabled } from "../store/permissionSelectors";
import {
  BriefcaseIcon,
  CheckIcon,
  SearchNormalIcon,
  ShieldIcon,
  Profile2userIcon,
  UserIcon,
  BarChartIcon,
  NotificationIcon,
  Grid4Icon,
  FolderOpenIcon,
  TagIcon,
  MessageTextIcon,
  Speakerphone2Icon,
  PeopleIcon,
  Data2Icon,
  ThumbsUpIcon,
  ImportIcon,
} from "@sumprio/ui/icons";

export type FormatMessage = (_descriptor: { id: string }) => string;

export type UserPermissions = {
  permissions?: {
    search?: boolean;
    view_client_cases?: boolean;
    view_clients?: boolean;
    create_case?: boolean;
    create_client?: boolean;
    create_categories?: boolean;
    roles?: boolean;
    create_users?: boolean;
    tags?: boolean;
    view_roles?: boolean;
  };
} | null;

export function getSysadminMenuItems(_formatMessage: FormatMessage): SideMenuItem[] {
  return [
    { id: "/businesses", label: "Businesses", icon: BriefcaseIcon },
    { id: "/Feedback", label: "Feedback", icon: ThumbsUpIcon },
    {
      id: "/TermsAndConditions",
      label: "Terms & Conditions",
      icon: CheckIcon,
    },
    {
      id: "/notification",
      label: "Notifications",
      icon: NotificationIcon,
    },
    {
      id: "/category-templates",
      label: "Category",
      icon: Data2Icon,
    },
  ];
}

export function getBusinessUserMenuItems(
  formatMessage: FormatMessage,
  userPermissions: UserPermissions
): SideMenuItem[] {
  const items: SideMenuItem[] = [];

  items.push({
    id: "/search",
    label: "Dashboard",
    icon: Grid4Icon,
  });
  if (permissionFlagEnabled(userPermissions, "view_client_cases")) {
    items.push({
      id: "/cases",
      label: formatMessage({ id: "casesLink" }),
      icon: FolderOpenIcon,
    });
  }
  if (permissionFlagEnabled(userPermissions, "view_clients")) {
    items.push({
      id: "/clients",
      label: formatMessage({ id: "clientsLink" }),
      icon: Profile2userIcon,
    });
  }
  items.push({
    id: "/reports",
    label: formatMessage({ id: "reportsLink" }),
    icon: BarChartIcon,
  });
  items.push({
    id: "/downloads",
    label: "Downloads",
    icon: ImportIcon,
  });
  if (permissionFlagEnabled(userPermissions, "tags")) {
    items.push({
      id: "/tags",
      label: formatMessage({ id: "tagsLink" }),
      icon: TagIcon,
    });
  }

  items.push({ id: "_div1", label: "", divider: true } as SideMenuItem);

  items.push({ id: "/chat", label: "Chat", icon: MessageTextIcon });
  items.push({
    id: "/notice-board",
    label: "Notice Board",
    icon: Speakerphone2Icon,
  });
  items.push({
    id: "/groups",
    label: "Groups",
    icon: PeopleIcon,
  });
  if (permissionFlagEnabled(userPermissions, "create_categories")) {
    items.push({
      id: "/categories",
      label: "Category",
      icon: Data2Icon,
    });
  }
  if (permissionFlagEnabled(userPermissions, "roles")) {
    items.push({
      id: "/roles",
      label: formatMessage({ id: "rolesLink" }),
      icon: ShieldIcon,
    });
  }
  if (permissionFlagEnabled(userPermissions, "create_users")) {
    items.push({
      id: "/users",
      label: formatMessage({ id: "usersLink" }),
      icon: Profile2userIcon,
    });
  }

  items.push({ id: "_div2", label: "", divider: true } as SideMenuItem);
  items.push({ id: "/feedback", label: "Feedback", icon: ThumbsUpIcon });
  return items;
}

export function getInactiveBusinessUserMenuItems(formatMessage: FormatMessage): SideMenuItem[] {
  return [
    {
      id: "/search",
      label: formatMessage({ id: "searchLink" }),
      icon: SearchNormalIcon,
    },
  ];
}

export function getClientMenuItems(formatMessage: FormatMessage): SideMenuItem[] {
  return [
    {
      id: "/search",
      label: formatMessage({ id: "searchLink" }),
      icon: SearchNormalIcon,
    },
    {
      id: "/cases",
      label: formatMessage({ id: "casesLink" }),
      icon: FolderOpenIcon,
    },
    {
      id: "/downloads",
      label: "Downloads",
      icon: ImportIcon,
    },
    { id: "_div1", label: "", divider: true } as SideMenuItem,
    { id: "/profile", label: "Account", icon: UserIcon },
    { id: "_div2", label: "", divider: true } as SideMenuItem,
    {
      id: "/notice-board",
      label: "Notice Board",
      icon: Speakerphone2Icon,
    },
    { id: "/chat", label: "Chat", icon: MessageTextIcon },
  ];
}

export function getTrpMenuItems(formatMessage: FormatMessage): SideMenuItem[] {
  return [
    {
      id: "/search",
      label: formatMessage({ id: "searchLink" }),
      icon: SearchNormalIcon,
    },
    {
      id: "/cases",
      label: formatMessage({ id: "casesLink" }),
      icon: FolderOpenIcon,
    },
    { id: "/clients", label: "Clients", icon: Profile2userIcon },
  ];
}

export type LayoutRole =
  | "sysadmin"
  | "business-active"
  | "business-inactive"
  | "client"
  | "trp"
  | null;

export function getMenuItemsForRole(
  role: LayoutRole,
  formatMessage: FormatMessage,
  userPermissions: UserPermissions
): SideMenuItem[] {
  switch (role) {
    case "sysadmin":
      return getSysadminMenuItems(formatMessage);
    case "business-active":
      return getBusinessUserMenuItems(formatMessage, userPermissions);
    case "business-inactive":
      return getInactiveBusinessUserMenuItems(formatMessage);
    case "client":
      return getClientMenuItems(formatMessage);
    case "trp":
      return getTrpMenuItems(formatMessage);
    default:
      return [];
  }
}
